import * as React from 'react';
import { logError } from 'remoteLogging';
import { ErrorLevel } from 'Loggers/LoggingProvider';
import { useUserContext } from 'Workspace/UserContext';

export interface UserLoginAwareProps {
	/**
	 * Children to render only after user has logged in
	 */
	children: React.ReactNode;
	/**
	 * Optional fallback component to render while checking login status or when user is not logged in
	 */
	fallback?: React.ReactNode;
}

/**
 * A wrapper component that conditionally renders its children based on whether the user has logged in.
 * This component checks the user's login status using the userContext and only renders children
 * when the user is authenticated.
 *
 * @example
 * ```tsx
 * <UserLoginAware>
 *   <CustomBlockingNotification userId={userId} />
 * </UserLoginAware>
 * ```
 *
 * @example With fallback
 * ```tsx
 * <UserLoginAware fallback={<LoadingSpinner />}>
 *   <CustomBlockingNotification userId={userId} />
 * </UserLoginAware>
 * ```
 */
export const UserLoginAware: React.FC<UserLoginAwareProps> = ({
	children,
	fallback = null,
}) => {
	const userContext = useUserContext();
	const [hasLoggedIn, setHasLoggedIn] = React.useState<boolean>(false);
	const [isChecking, setIsChecking] = React.useState<boolean>(true);

	React.useEffect(() => {
		let isMounted = true;

		const checkLoginStatus = async () => {
			try {
				const loggedIn = await userContext.hasLoggedIn();
				if (isMounted) {
					setHasLoggedIn(loggedIn);
					setIsChecking(false);
				}
			} catch (error) {
				if (isMounted) {
					logError(error, {
						additionalContext: { level: ErrorLevel.CRITICAL },
					});
					setHasLoggedIn(false);
					setIsChecking(false);
				}
			}
		};

		checkLoginStatus();

		return () => {
			isMounted = false;
		};
	}, [userContext]);

	if (isChecking) {
		return <>{fallback}</>;
	}

	if (!hasLoggedIn) {
		return <>{fallback}</>;
	}

	return <>{children}</>;
};
